import { useFormikContext } from 'formik';
import Parser from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useGetAreaBaseBlocks } from 'pages/documents_v2/hooks/useGetAreaBaseBlocks';
import { Skeleton } from 'components/Skeleton';
import { useParams } from 'react-router-dom';
import HelperFunctions from 'pages/global/HelperFunctions';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { useGetSections } from 'pages/documents_v2/hooks/useGetSections';
import { ChevronRight, Link45deg } from 'react-bootstrap-icons';
import { set } from 'features/app/appSlice';
import { useDispatch } from 'react-redux';
import { useContext, useMemo } from 'react';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';

export function LinkedBlock({ block, baseBlock, index, readOnly = false }) {
    const { getFieldMeta } = useFormikContext();
    const fieldMeta = getFieldMeta('areaBlocks');
    const initialBlock = fieldMeta.initialValue.find((_block) => _block.key === block.key);

    // The content of the linked block is stored in the baseBlocks
    const linkedBlock = useGetLinkedBlock(block.key);

    const isEmpty = (block.latestContent === '' && !linkedBlock) || (linkedBlock && linkedBlock.latestContent === '');
    const isDirty = !readOnly && (block.id === null || block.latestContent !== initialBlock?.latestContent);

    if (readOnly) {
        return <>{renderBlockContent()}</>;
    }

    return (
        <ClickableBlock block={block} index={index}>
            {renderBlockContent()}
        </ClickableBlock>
    );

    function renderBlockContent() {
        return (
            <div className="p-4 bg-light-gray" style={{ borderRadius: '0.25rem' }}>
                {isEmpty ? (
                    <EmptyBlockWarning />
                ) : isDirty ? (
                    <PreviewNotAvailableWarning />
                ) : (
                    <RenderedLinkedBlock block={block} linkedBlock={linkedBlock} baseBlock={baseBlock} />
                )}
            </div>
        );
    }
}

function RenderedLinkedBlock({ block, linkedBlock, baseBlock }) {
    const { hideChanges } = useContext(EditAreaContext);

    const htmlToRender = useMemo(() => {
        if (baseBlock && !hideChanges) {
            if (baseBlock.hasOwnProperty('changes') && baseBlock.changes.length > 0) {
                return baseBlock.renderedDiffContent;
            }

            if (baseBlock.hasOwnProperty('latestContentRendered') && baseBlock.latestContentRendered.length > 0) {
                return baseBlock.latestContentRendered;
            }
        }

        if (linkedBlock) {
            return linkedBlock.latestContent;
        }

        return undefined;
    }, [baseBlock, linkedBlock, hideChanges]);

    return (
        <div>
            <LinkedBlockBreadcrumb block={block} />

            <div className="tiny-mce-content-body-linked">
                {htmlToRender ? <>{Parser(htmlToRender)}</> : <Skeleton />}
            </div>
        </div>
    );
}

function ClickableBlock({ block, index, children }) {
    const dispatch = useDispatch();

    return (
        <div className="cursor-pointer" onClick={handleClick}>
            {children}
        </div>
    );

    function handleClick(e) {
        e.preventDefault();

        dispatch(
            set({
                key: 'blockEditModal',
                value: {
                    eventKey: 'linkedBlockModal',
                    block,
                    blockIndex: index,
                },
            }),
        );
    }
}

function LinkedBlockBreadcrumb({ block }) {
    const { documentId } = useParams();
    const data = HelperFunctions.tryParseJSON(block.latestContent, undefined);
    const blockDocumentId = data?.documentId ?? documentId;
    const document = useGetDocument(data?.documentId);
    const sections = useGetSections(document?.id, true);

    if (!document) {
        return null;
    }

    const area = HelperFunctions.findInDocument({ type: 'area', id: data.areaId }, sections);

    if (area === undefined) {
        return null;
    }

    const section = HelperFunctions.findInDocument({ type: 'section', id: area.sectionId }, sections);

    if (section === undefined) {
        return null;
    }

    return (
        <div className="pb-4 small text-muted">
            <Link45deg size={14} className="mr-2" />

            {parseInt(documentId) !== parseInt(blockDocumentId) && (
                <>
                    {document.name}
                    <ChevronRight className="mx-1" />
                </>
            )}

            {section.title}
            <ChevronRight className="mx-1" />
            {area.title}
        </div>
    );
}

function EmptyBlockWarning() {
    const { t } = useTranslation();

    return (
        <div className="small">
            <Link45deg size={14} className="mr-2" />
            {t('documents:document.navbar.main.editor.left.blocks.types.linked.linkedBlockEmpty')}
        </div>
    );
}

function PreviewNotAvailableWarning() {
    const { t } = useTranslation();

    return (
        <div className="small text-secondary">
            {t('documents:document.navbar.main.editor.left.blocks.types.linked.linkedBlockPreview')}
        </div>
    );
}

function useGetLinkedBlock(blockKey) {
    const { baseBlocks } = useGetAreaBaseBlocks();

    return baseBlocks.find((baseBlock) => baseBlock.key === blockKey);
}
